import EMOJI_SHORTCODES from "markdown-it-emoji/lib/data/full.mjs";

/**
 * The shortcode dictionary the parser validates `:name:` against
 * (`docs/spec/markdown-dialect.md` §6.13), exposed so that a client can offer
 * the same set in an emoji picker. Shared here rather than re-derived in the
 * client, so the picker can never offer a shortcode the parser would reject.
 */
export const EMOJI_BY_SHORTCODE: Readonly<Record<string, string>> = EMOJI_SHORTCODES;

export interface EmojiEntry {
  /** The shortcode, without colons — the `data-name` attribute of an `emoji` node. */
  name: string;
  /** The character sequence to render. */
  character: string;
}

/** The emoji for a shortcode, or `null` when the shortcode is unknown. */
export function emojiCharacter(name: string): string | null {
  return Object.prototype.hasOwnProperty.call(EMOJI_SHORTCODES, name)
    ? (EMOJI_SHORTCODES[name] ?? null)
    : null;
}

/**
 * Shortcode search for a picker: a case-insensitive substring match, with
 * shortcodes that *start* with the query ranked ahead of those that merely
 * contain it, then alphabetically. An empty query returns the first `limit`
 * shortcodes in dictionary order.
 */
export function searchEmoji(query: string, limit = 25): EmojiEntry[] {
  const needle = query.trim().toLowerCase();
  const entries: EmojiEntry[] = [];
  for (const [name, character] of Object.entries(EMOJI_SHORTCODES)) {
    if (needle === "" || name.includes(needle)) entries.push({ name, character });
  }
  if (needle !== "") {
    entries.sort((a, b) => {
      const aStarts = a.name.startsWith(needle);
      const bStarts = b.name.startsWith(needle);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }
  return entries.slice(0, limit);
}
